"use client";

import React, { useState, useMemo } from "react";
import {
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment-timezone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useToast } from "@/utils/theme/useToast";

const StyledSelect = styled(Select)(({ theme }) => ({
  height: 32,
  fontSize: "0.875rem",
  "& .MuiOutlinedInput-notchedOutline": {
    border: `0px solid ${theme.palette.grey[300]}`,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: `0px solid ${theme.palette.grey[400]}`,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: `0px solid ${theme.palette.primary.main}`,
  },
  "& .MuiSelect-icon": {
    display: "none",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "0 !important",
  },
  "&.Mui-disabled": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `0px solid ${theme.palette.grey[200]}`,
    },
    "& .MuiSelect-select": {
      cursor: "default",
      WebkitTextFillColor: theme.palette.text.disabled,
    },
  },
}));

const SearchInput = styled(TextField)(({ theme }) => ({
  width: "calc(100% - 24px)",
  margin: "12px",
  "& .MuiOutlinedInput-root": {
    height: "40px",
    fontSize: "0.875rem",
    borderRadius: "6px",
    backgroundColor: "transparent!important",
    "& fieldset": {
      border: `1px solid ${theme.palette.grey[300]}`,
    },
    "&:hover fieldset": {
      border: `1px solid ${theme.palette.grey[400]}`,
    },
    "&.Mui-focused fieldset": {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 14px",
    fontSize: "0.875rem",
    backgroundColor: "transparent!important",
  },
}));

const getFormattedTimezones = () => {
  const timezoneNames = {
    "America/New_York": "Eastern Time",
    "America/Chicago": "Central Time",
    "America/Denver": "Mountain Time",
    "America/Los_Angeles": "Pacific Time",
    "America/Phoenix": "Mountain Standard Time",
    "America/Anchorage": "Alaska Time",
    "Pacific/Honolulu": "Hawaii Time",
    "Europe/London": "Greenwich Mean Time",
    "Europe/Paris": "Central European Time",
    "Europe/Berlin": "Central European Time",
    "Europe/Rome": "Central European Time",
    "Europe/Madrid": "Central European Time",
    "Europe/Amsterdam": "Central European Time",
    "Asia/Tokyo": "Japan Standard Time",
    "Asia/Shanghai": "China Standard Time",
    "Asia/Kolkata": "India Standard Time",
    "Asia/Dubai": "Gulf Standard Time",
    "Australia/Sydney": "Australian Eastern Time",
    "Australia/Melbourne": "Australian Eastern Time",
    "Australia/Perth": "Australian Western Time",
  };

  return moment.tz
    .names()
    .filter((tz) => {
      try {
        const zone = moment.tz.zone(tz);
        return zone && !tz.includes("SystemV") && !tz.includes("Etc/");
      } catch {
        return false;
      }
    })
    .map((tz) => {
      try {
        const now = moment().tz(tz);
        const offsetMinutes = now.utcOffset();
        const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
        const offsetMins = Math.abs(offsetMinutes % 60);

        const offsetStr = `${offsetMinutes >= 0 ? "+" : "-"}${offsetHours
          .toString()
          .padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`;

        const friendlyName =
          timezoneNames[tz] ||
          tz.split("/").pop()?.replace(/_/g, " ") ||
          tz.replace(/_/g, " ");

        return {
          value: tz,
          label: `UTC ${offsetStr} ${friendlyName}`,
          offset: offsetMinutes,
          currentTime: now.format("HH:mm"),
          searchText: `${friendlyName} ${tz} ${offsetStr}`.toLowerCase(),
        };
      } catch {
        return null;
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.offset - b.offset);
};

const TimezoneSelector = ({
  currentTimezone,
  onTimezoneChange,
  sx = {},
  disabled = false,
  variant = "select",
  open = false,
  autoFocus = false,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const selectedTimezoneRef = React.useRef(null);
  const theme = useTheme();
  const toast = useToast();

  const allTimezones = useMemo(() => getFormattedTimezones(), []);

  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return allTimezones;

    const search = searchTerm.toLowerCase();
    return allTimezones.filter((timezone) =>
      timezone.searchText.includes(search),
    );
  }, [allTimezones, searchTerm]);

  const getCurrentTimezoneDisplay = () => {
    const found = allTimezones.find((tz) => tz.value === currentTimezone);
    if (found) {
      return found.label.length > 25
        ? `${found.label.substring(0, 25)}...`
        : found.label;
    }

    if (moment.tz.zone(currentTimezone)) {
      const now = moment().tz(currentTimezone);
      const offsetMinutes = now.utcOffset();
      const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
      const offsetMins = Math.abs(offsetMinutes % 60);
      const offsetStr = `${offsetMinutes >= 0 ? "+" : "-"}${offsetHours
        .toString()
        .padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`;

      const friendlyName =
        currentTimezone.split("/").pop()?.replace("_", " ") || currentTimezone;
      const displayText = `UTC ${offsetStr} ${friendlyName}`;

      return displayText.length > 25
        ? `${displayText.substring(0, 25)}...`
        : displayText;
    }

    return "Invalid Timezone";
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const scrollSelectedTimezoneIntoView = () => {
    selectedTimezoneRef.current?.scrollIntoView({ block: "center" });
  };

  const selectTimezone = (newTimezone) => {
    if (disabled) return;

    try {
      onTimezoneChange(newTimezone);
      setSearchTerm("");
    } catch (error) {
      console.error("Error changing timezone:", error);

      // Revert on error (if onTimezoneChange allows)
      toast.show(
        "Timezone Error",
        "Unable to change timezone. Please try a different selection.",
      );
    }
  };

  const handleChange = (e) => {
    selectTimezone(e.target.value);
  };

  const handleOpen = () => {
    if (disabled) {
      toast.show("Timezone Locked", "Timezone cannot be changed at this time.");
      return false;
    }
    return true;
  };

  const handleClose = () => {
    setSearchTerm("");
    onClose?.();
  };

  const isValidTimezone = moment.tz.zone(currentTimezone) !== null;

  // Show error toast for invalid timezone
  React.useEffect(() => {
    if (currentTimezone && !isValidTimezone) {
      toast.show(
        "Invalid Timezone",
        "The current timezone is not recognized. Please select a valid timezone.",
      );
    }
  }, [currentTimezone, isValidTimezone, toast]);

  if (variant === "display") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
        <AccessTimeIcon
          sx={{
            fontSize: "1.2rem",
            color: disabled ? "text.disabled" : "text.secondary",
            mr: 1,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            color: disabled ? "text.disabled" : "inherit",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "200px",
          }}
          title={getCurrentTimezoneDisplay().replace("...", "")}
        >
          {getCurrentTimezoneDisplay()}
        </Typography>
      </Box>
    );
  }

  if (variant === "autocomplete") {
    return (
      <Autocomplete
        options={allTimezones}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        value={allTimezones.find((tz) => tz.value === currentTimezone) ?? null}
        onChange={(_, newValue) => {
          if (!newValue) return;
          selectTimezone(newValue.value);
        }}
        onClose={handleClose}
        open={open}
        disabled={disabled}
        disableClearable
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Timezone"
            variant="outlined"
            size="small"
            placeholder="Search timezone..."
            autoFocus={autoFocus}
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <Box
            key={option.value}
            component="li"
            {...props}
            sx={{ justifyContent: "space-between", gap: 2 }}
          >
            <Typography variant="body2">{option.label}</Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", opacity: 0.8 }}
            >
              {option.currentTime}
            </Typography>
          </Box>
        )}
        slotProps={{
          paper: { sx: { borderRadius: 0, mt: 0.5 } },
          listbox: { sx: { maxHeight: 300, p: 0 } },
        }}
        sx={{ width: { xs: "100%", md: 280 }, ...sx }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
      <FormControl size="small">
        <StyledSelect
          value={isValidTimezone ? currentTimezone : "America/New_York"}
          onChange={handleChange}
          onOpen={handleOpen}
          onClose={handleClose}
          disabled={disabled}
          displayEmpty
          renderValue={(value) => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AccessTimeIcon
                sx={{
                  fontSize: "1.2rem",
                  color: disabled ? "text.disabled" : "text.secondary",
                  mr: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: disabled ? "text.disabled" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "200px",
                }}
                title={getCurrentTimezoneDisplay().replace("...", "")}
              >
                {getCurrentTimezoneDisplay()}
              </Typography>
            </Box>
          )}
          MenuProps={{
            slotProps: {
              transition: {
                onEntered: scrollSelectedTimezoneIntoView,
              },
              paper: {
                sx: {
                  maxHeight: 275,
                  borderRadius: "6px",
                  "& .MuiList-root": {
                    padding: 0,
                  },
                },
              },
            },
          }}
        >
          {/* Search input */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.background.paper,
              zIndex: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              borderRadius: "0px!important",
            }}
          >
            <SearchInput
              placeholder="Search timezones..."
              value={searchTerm}
              onChange={handleSearchChange}
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: "1.1rem", color: "text.secondary" }}
                      />
                    </InputAdornment>
                  ),
                },
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                // Show helper toast on first search
                if (e.key.length === 1 && searchTerm.length === 0) {
                  toast.show(
                    "Search Tips",
                    "Try searching by city name, country, or timezone offset (e.g., 'New York', 'UTC+5').",
                  );
                }
              }}
            />
          </Box>

          {/* Timezone list */}
          {filteredTimezones.length > 0 ? (
            filteredTimezones.map((timezone) => (
              <MenuItem
                key={timezone.value}
                ref={
                  timezone.value === currentTimezone
                    ? selectedTimezoneRef
                    : null
                }
                value={timezone.value}
                selected={timezone.value === currentTimezone}
                sx={{
                  fontSize: "0.825rem",
                  py: 1,
                  pl: 4,
                  pr: 2,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: theme.palette.action.focus,
                  },
                  "&.Mui-selected": {
                    backgroundColor: `${theme.palette.primary.main}08`,
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}12`,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {timezone.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      ml: 1,
                      opacity: 0.8,
                    }}
                  >
                    {timezone.currentTime}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled sx={{ py: 2, justifyContent: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  No timezones found
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  Try searching by city or country name
                </Typography>
              </Box>
            </MenuItem>
          )}
        </StyledSelect>
      </FormControl>
    </Box>
  );
};

export default TimezoneSelector;
